import React, { useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Group } from '../containers/Group/Group.component';

// Transparent grab strip pinned to the resizable edge; on hover/drag it only reveals a thin accent
// line at the very edge instead of a wide filled bar, so it never covers the panel's content.
const StyledResizeGroup = styled(Group).attrs({ draggable: false })`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 8px;
  padding: 0px;
  cursor: col-resize;
  background-color: transparent;

  ::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background-color: ${({ theme }) => theme.colors.gray[400]};
    opacity: 0;
    transition: opacity 0.1s linear;
  }

  :hover::after,
  :active::after {
    opacity: 1;
  }
`;

interface IResizeHandlerProps<T extends HTMLElement> {
  resizable: React.RefObject<T | null>;
  onUpdate?: (width: number, isResizing: boolean) => void;
}

export const ResizeHandler = <T extends HTMLElement>({ resizable, onUpdate }: IResizeHandlerProps<T>) => {
  const resizerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (resizable.current) {
        const newWidth = startWidth.current + (startX.current - event.clientX);

        if (newWidth > 0) {
          resizable.current.style.width = `${newWidth}px`;
          onUpdate?.(newWidth, true);
        }
      }
    },
    [onUpdate, resizable.current],
  );

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    if (onUpdate && resizable.current) {
      onUpdate(resizable.current.clientWidth, false);
    }

    // Re-enable pointer events after dragging
    document.body.style.pointerEvents = 'auto';
  }, [handleMouseMove, onUpdate, resizable.current]);

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      startX.current = event.clientX;
      startWidth.current = resizable.current?.clientWidth || 0;

      // Disable all pointer events when dragging
      document.body.style.pointerEvents = 'none';

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [handleMouseMove, handleMouseUp],
  );

  useEffect(() => {
    const resizer = resizerRef.current;

    if (resizer) {
      resizer.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      if (resizer) {
        resizer.removeEventListener('mousedown', handleMouseDown);
      }

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp]);

  return <StyledResizeGroup ref={resizerRef} vertical subject="resizer" />;
};
